import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { useCheckApptEligibilityMutation } from 'services/appointments/appointments';
import { useCheckPrescriptionEligibilityMutation } from 'services/patientChart/patientChart';
import { useCheckProgramEligibilityMutation } from 'services/programs/programs';
import { useCheckForApptAuthEligibilityMutation } from 'services/scheduling/scheduling';
import { useCheckSurveysEligibilityMutation } from 'services/surveys/surveys';

import { selectUser } from 'store';
import { setModal } from 'store/modals/modalSlice';

import { useAppDispatch, useAppSelector } from 'hooks';

import { notifyError } from 'shared/Toast/Toast';
import { logDataDogEvent } from 'utils/datadog';

import { hasPaymentMethodOnFile, resolvePaywallError } from './eligibilityAccess';

// import { useAnalytics } from 'hooks/useAnalytics';
import {
  AppointmentEligibilityAuthPayload,
  AppointmentEligibilityPayload,
  CheckApptAuthFnReturn,
  CheckApptFnReturn,
  CheckOnboardingFnReturn,
  CheckPrescriptionFnReturn,
  CheckSurveyFnReturn,
  ELIGIBILITY_UNAVAILABLE,
  EligibilityUnavailable,
  OnboardingEligibilityPayload,
  PaywallContext,
  PrescriptionEligibilityPayload,
  SurveyEligibilityPayload
} from './usePaywall.types';

/**
 * Generic Type Guard for 403 Errors.
 * It checks if the error has status 403 and asserts that 'data' matches type T.
 * T is for expected data type in the 403 error response.
 */
export function isPaywallError<T>(
  error: unknown
): error is FetchBaseQueryError & { data: T; status: 403 } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    error.status === 403 &&
    'data' in error
  );
}

export const usePaywall = () => {
  const dispatch = useAppDispatch();
  const { paymentProfiles } = useAppSelector(selectUser) ?? {};
  const hasPaymentMethod = hasPaymentMethodOnFile(paymentProfiles);
  // const logEvent = useAnalytics();

  const [checkApptEligibility, { isLoading }] = useCheckApptEligibilityMutation();
  const [checkApptEligibilityAuth, { isLoading: isLoadingApptAuth }] =
    useCheckForApptAuthEligibilityMutation();
  const [checkSurveysEligibility, { isLoading: isLoadingSurveys }] =
    useCheckSurveysEligibilityMutation();
  const [checkPrescriptionEligibility, { isLoading: isLoadingPrescription }] =
    useCheckPrescriptionEligibilityMutation();
  const [checkProgramEligibility, { isLoading: isLoadingPrograms }] =
    useCheckProgramEligibilityMutation();

  // we need to overload the function signature to support different payloads and return types
  function checkEligibility(
    payload: AppointmentEligibilityPayload
  ): Promise<CheckApptFnReturn | 'OK'>;
  function checkEligibility(
    payload: SurveyEligibilityPayload
  ): Promise<CheckSurveyFnReturn | EligibilityUnavailable | 'OK'>;
  function checkEligibility(
    payload: PrescriptionEligibilityPayload
  ): Promise<CheckPrescriptionFnReturn | 'OK'>;
  function checkEligibility(
    payload: OnboardingEligibilityPayload
  ): Promise<CheckOnboardingFnReturn | 'OK'>;
  function checkEligibility(
    payload: AppointmentEligibilityAuthPayload
  ): Promise<CheckApptAuthFnReturn | 'OK'>;

  // Implementation
  async function checkEligibility(
    payload:
      | AppointmentEligibilityPayload
      | SurveyEligibilityPayload
      | PrescriptionEligibilityPayload
      | OnboardingEligibilityPayload
      | AppointmentEligibilityAuthPayload
  ): Promise<
    | CheckApptFnReturn
    | CheckSurveyFnReturn
    | CheckPrescriptionFnReturn
    | CheckOnboardingFnReturn
    | CheckApptAuthFnReturn
    | EligibilityUnavailable
    | 'OK'
  > {
    // helper function to throw a generic error
    const throwGenericError = () => {
      throw new Error('Error checking eligibility');
    };

    switch (payload.type) {
      case 'appointment': {
        const { error } = await checkApptEligibility({
          appointmentTypeCode: payload.appointmentTypeCode
        });

        if (!error) return 'OK';
        if (isPaywallError<CheckApptFnReturn>(error)) {
          return error.data;
        }
        return throwGenericError();
      }

      case 'survey': {
        const { type, ...rest } = payload;
        const { error } = await checkSurveysEligibility(rest);

        if (!error) return 'OK';
        if (isPaywallError<CheckSurveyFnReturn>(error)) {
          return error.data;
        }

        logDataDogEvent({
          error: error as Error,
          level: 'warn',
          message: 'Survey eligibility check failed'
        });

        const errorData = 'data' in error ? error.data : undefined;
        const errorMessage =
          errorData !== null && typeof errorData === 'object' && 'message' in errorData
            ? errorData.message
            : undefined;
        const isMissingMedication =
          'status' in error &&
          ((error.status === 404 && errorMessage === 'Medication not found') ||
            (error.status === 409 &&
              errorMessage === 'Renewal eligibility could not be determined'));

        notifyError(
          isMissingMedication
            ? 'We couldn’t find the medication linked to your prescription. Please message your care team for help starting your renewal.'
            : 'We’re unable to start your renewal because some information is unavailable. Please try again later or message your care team.'
        );
        return ELIGIBILITY_UNAVAILABLE;
      }

      case 'prescription': {
        const { type, ...rest } = payload;
        const { error } = await checkPrescriptionEligibility(rest);
        if (!error) return 'OK';
        if (isPaywallError<CheckPrescriptionFnReturn>(error)) {
          return error.data;
        }

        return throwGenericError();
      }

      case 'onboarding': {
        const { type, ...rest } = payload;
        const { data } = await checkProgramEligibility({ ...rest });
        if (!data?.data.action) return 'OK';
        return data;
      }

      // for appointments eligibility check during auth flow we have a separate logic - no 403 is returned when eligible
      case 'appointment-auth': {
        const { data } = await checkApptEligibilityAuth({
          appointmentTypeCode: payload.appointmentTypeCode
        });

        if (!data) {
          return throwGenericError();
        }
        return data;
      }

      default:
        throw new Error('Invalid payload type');
    }
  }
  const handlePaywallError = (error: unknown, data: PaywallContext) => {
    const resolution = resolvePaywallError(error, hasPaymentMethod);

    switch (resolution.type) {
      case 'open-link':
        window.location.assign(resolution.link);
        return;
      case 'program-paywall':
        dispatch(
          setModal({
            key: 'programPaywall',
            value: {
              ctx: data.ctx,
              programCategory: resolution.programCategory,
              show: true
            }
          })
        );
        return;
      case 'program-resume':
        dispatch(
          setModal({
            key: 'resumeToProgram',
            value: {
              programIds: resolution.programIds,
              show: true
            }
          })
        );
        return;
      case 'open-payment-method':
        dispatch(
          setModal({
            key: 'paymentPastDue',
            value: {
              show: true
            }
          })
        );
        return;
      default:
        return;
    }
  };

  const loading =
    isLoading ||
    isLoadingSurveys ||
    isLoadingPrescription ||
    isLoadingPrograms ||
    isLoadingApptAuth;

  return { checkEligibility, handlePaywallError, loading };
};
